import './case-list-page.css';
import { useEffect, useState } from 'react';
import { Header } from '../header/header';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { GetAccessToken } from '../../utils/auth-utils';
import { bookingPageService } from '../../services/booking-page-service';
import { App, Button, Statistic, Typography, Tooltip, DatePicker } from 'antd';
import { handleDownloadAll } from '../../utils/file-utils';
import { reportingService } from '../../services/reporting-service';
import { AnalyticsDownload } from '../admin-page/components/analytics/analytics-download';
import { getBookingReservationsData, getQualifyingAnswersExport } from '../../services/admin-page-service';
import { QualifyingAnswerExport } from '../../dto/admin-config-models';
const { RangePicker } = DatePicker;

const flattenQualifyingAnswers = (data: QualifyingAnswerExport[]) => {
  const allQuestionKeys = Array.from(new Set(data.flatMap((item) => Object.keys(item.answers)))).sort();

  return data.map((item) => {
    const flattened: Record<string, string> = {};
    allQuestionKeys.forEach((key) => {
      flattened[key] = item.answers[key] ?? '';
    });
    flattened['opportunityInternalId'] = item.opportunityInternalId ?? '';
    flattened['signupId'] = item.signupId ?? '';
    flattened['createdDate'] = item.createdDate ?? '';
    return flattened;
  });
};

export const CaseListPage = () => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [isGamificationLoading, setIsGamificationLoading] = useState(false);
  const [isGamificationContactLoading, setIsGamificationContactLoading] = useState(false);
  const [gamifiedUsersCount, setGamifiedUsersCount] = useState<number | string>('Calculating...');
  const [totalOppsCount, setTotalOppsCount] = useState<number | string>('Calculating...');
  const [isBookingReservationsLoading, setIsBookingReservationsLoading] = useState(false);
  const [reservationRange, setReservationRange] = useState<[Date | null, Date | null]>([null, null]);
  const [isQualifyingAnswersLoading, setIsQualifyingAnswersLoading] = useState(false);
  const [qualifyingAnswersRange, setQualifyingAnswersRange] = useState<[Date | null, Date | null]>([null, null]);

  useEffect(() => {
    (async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const totalOpps = await bookingPageService.getAllCases(token!.accessToken, true);
      setTotalOppsCount(totalOpps ? totalOpps.count : 0);
      const gamifiedUsers = await reportingService.getGamifiedUsersCountAsync(token.accessToken);
      setGamifiedUsersCount(gamifiedUsers);
    })();
  }, []);

  const downloadOpps = async () => {
    setIsLoading(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      await bookingPageService.downloadAllCasesAsCsv(token!.accessToken, 'opportunity_queue_bookingos');
    } catch (err) {
      notification.error({
        message: 'Download failed',
        description: err instanceof Error ? err.message : 'Could not download opportunities.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadReport = async () => {
    setIsGamificationLoading(true);
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    await reportingService.downloadReport(token.accessToken);
    setIsGamificationLoading(false);
  };

  const downloadContactEdits = async () => {
    setIsGamificationContactLoading(true);
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const editContactsResult = await bookingPageService.getContactsToEdit(token.accessToken, null);
    if (!editContactsResult.isSuccess) {
      notification.error({
        message: 'Error',
        description: 'Could not download the contacts to edit',
      });
      return;
    }
    handleDownloadAll(editContactsResult.data, 'contacts_to_edit');
    setIsGamificationContactLoading(false);
  };

  const downloadBookingReservations = async () => {
    if (!reservationRange[0] || !reservationRange[1]) return;

    setIsBookingReservationsLoading(true);

    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const startDateStr = reservationRange[0] ? reservationRange[0].toISOString() : '';
      const endDateStr = reservationRange[1] ? reservationRange[1].toISOString() : '';
      const editContactsResult = await getBookingReservationsData(token.accessToken, startDateStr, endDateStr);
      if (!editContactsResult.isSuccess) {
        notification.error({
          message: 'Error',
          description: 'Could not download the contacts to edit',
        });
        return;
      }
      if (editContactsResult.data.length === 0) {
        notification.error({
          message: 'No Booking Reservations found',
          description: 'Found no booking reservations to download',
        });
        return;
      }
      handleDownloadAll(editContactsResult.data, `booking_reservations_${startDateStr}_${endDateStr}`);
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Could not download the contacts to edit. Error: ${e}`,
      });
    } finally {
      setIsBookingReservationsLoading(false);
    }
  };

  const downloadQualifyingAnswers = async () => {
    if (!qualifyingAnswersRange[0] || !qualifyingAnswersRange[1]) return;

    setIsQualifyingAnswersLoading(true);

    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const startDateStr = qualifyingAnswersRange[0].toISOString();
      const endDateStr = qualifyingAnswersRange[1].toISOString();
      const result = await getQualifyingAnswersExport(token.accessToken, startDateStr, endDateStr);
      if (!result.isSuccess) {
        notification.error({
          message: 'Error',
          description: 'Could not download qualifying answers',
        });
        return;
      }
      if (result.data.length === 0) {
        notification.error({
          message: 'No Qualifying Answers found',
          description: 'Found no qualifying answers to download for the selected date range',
        });
        return;
      }
      const flattenedData = flattenQualifyingAnswers(result.data);
      handleDownloadAll(flattenedData, `qualifying_answers_${startDateStr.split('T')[0]}_${endDateStr.split('T')[0]}`);
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Could not download qualifying answers. Error: ${e}`,
      });
    } finally {
      setIsQualifyingAnswersLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="container">
        <h4>Opportunity Queue</h4>
        <div className="clp-horizontal-div">
          <Statistic title="Number of Callable Opportunities" value={totalOppsCount} valueStyle={{ fontSize: 16 }} style={{ alignSelf: 'center', margin: 5 }} />
          <Button className="clp-download-btn" onClick={downloadOpps} loading={isLoading}>
            Download Opportunity Queue
          </Button>
        </div>
      </div>
      <div className="container">
        <h4>Gamification User Progress</h4>
        <div className="clp-horizontal-div">
          <Statistic title="Number of Gamification Users" value={gamifiedUsersCount} valueStyle={{ fontSize: 16 }} style={{ alignSelf: 'center', margin: 5 }} />
          <Button className="clp-download-btn" onClick={downloadReport} loading={isGamificationLoading}>
            Download User Progress
          </Button>
        </div>
      </div>
      <div className="container">
        <h4>Sales Capacity</h4>
        <div className="clp-horizontal-div">
          <Typography.Text type="secondary" style={{ marginBottom: '1rem', display: 'block' }}>
            Download the number of available sales slots for the next 3 weeks.
          </Typography.Text>
          <AnalyticsDownload />
        </div>
      </div>
      <div className="container">
        <h4>Contacts to Edit</h4>
        <div className="clp-horizontal-div">
          <Typography.Text type="secondary" style={{ marginBottom: '1rem', display: 'block' }}>
            Download all the contacts that need to be edited in salesforce.
          </Typography.Text>
          <Button className="clp-download-btn" onClick={downloadContactEdits} loading={isGamificationContactLoading}>
            Download Edit Contacts
          </Button>
        </div>
      </div>

      <div className="container">
        <h4>Booking Reservations</h4>
        <Typography.Text type="secondary" style={{ marginBottom: '1rem', display: 'block' }}>
          Download all the Booking reservations based on the start and end times
        </Typography.Text>
        <div
          className="clp-horizontal-div"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <RangePicker value={reservationRange as any} onChange={(dates) => setReservationRange(dates as [any, any])} placeholder={['Start date', 'End date']} />
          <Tooltip title="Select a date range to download booking reservations">
            <Button className="clp-download-btn" onClick={downloadBookingReservations} loading={isBookingReservationsLoading} disabled={!reservationRange?.[0] || !reservationRange?.[1]}>
              Download Booking Reservations
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="container">
        <h4>Qualifying Question Outcomes</h4>
        <Typography.Text type="secondary" style={{ marginBottom: '1rem', display: 'block' }}>
          Download all qualifying question answers based on the start and end times
        </Typography.Text>
        <div
          className="clp-horizontal-div"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <RangePicker value={qualifyingAnswersRange as any} onChange={(dates) => setQualifyingAnswersRange(dates as [any, any])} placeholder={['Start date', 'End date']} />
          <Tooltip title="Select a date range to download qualifying question answers">
            <Button className="clp-download-btn" onClick={downloadQualifyingAnswers} loading={isQualifyingAnswersLoading} disabled={!qualifyingAnswersRange?.[0] || !qualifyingAnswersRange?.[1]}>
              Download Qualifying Answers
            </Button>
          </Tooltip>
        </div>
      </div>
    </>
  );
};
